import ActivityVisibility from "@mapmycustomers/shared/enum/activity/ActivityVisibility";
import ActivityCustomFieldValue from "@mapmycustomers/shared/types/customField/ActivityCustomFieldValue";
import CustomField from "@mapmycustomers/shared/types/customField/CustomField";
import { Activity } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import User from "@mapmycustomers/shared/types/User";

import localSettings from "@app/config/LocalSettings";
import RecurringIntervalType from "@app/enum/RecurringIntervalType";
import {
  parseActivityInterval,
  stringifyActivityInterval,
} from "@app/util/activity/activityInterval";
import { parseApiDate } from "@app/util/parsers";

import FormValues from "../types/FormValues";

import { predefinedIntervals } from "./useRecurIntervalOptions";

const getRecurIntervalType = (recurInterval: FormValues["recurInterval"]) => {
  if (!recurInterval) {
    return RecurringIntervalType.NEVER;
  }

  const recurIntervalString = stringifyActivityInterval(recurInterval);

  return (
    predefinedIntervals.find((intervalString) => intervalString === recurIntervalString) ??
    RecurringIntervalType.CUSTOM
  );
};

const getFormInitialValues = (
  activity: Partial<Omit<Activity, "id">> | undefined,
  currentUser: User,
  defaultActivityTypeId: ActivityType["id"] | undefined,
  defaultCompletion: boolean,
  defaultVisibility: ActivityVisibility,
  isCreateForm: boolean = false,
  unavailableActivityType?: Pick<ActivityType, "id" | "name">
): FormValues => {
  const customFields: Record<CustomField["esKey"] | CustomField["id"], ActivityCustomFieldValue> =
    {};
  activity?.customFields?.forEach(
    (customFieldValue) =>
      (customFields[isCreateForm ? customFieldValue.customField.id : customFieldValue.esKey] =
        customFieldValue)
  );

  const recurInterval = parseActivityInterval(activity?.recurInterval);
  let startAt: Date | undefined = new Date();
  if (activity) {
    if (activity.startAt) {
      startAt = parseApiDate(activity.startAt);
    } else {
      startAt = undefined;
    }
  }

  return {
    activityTypeId: unavailableActivityType
      ? undefined
      : (activity?.crmActivityType?.id ?? defaultActivityTypeId),
    allDay: activity?.allDay,
    assignee: activity?.assignee ?? currentUser,
    assigneeId: activity?.assignee?.id ?? currentUser.id,
    coachingNoteId: activity?.coachingNoteId ?? undefined,
    companyId: activity?.account?.id,
    completed: activity?.completed ?? defaultCompletion,
    customFields,
    dealId: activity?.deal?.id,
    endAt: activity?.endAt ? parseApiDate(activity.endAt) : undefined,
    files: activity?.files as File[] | undefined,
    isRecurrent: !!recurInterval,
    name: activity?.name ?? "",
    note: activity?.note,
    peopleIds:
      activity?.contacts?.map(({ id }) => id) ?? (activity?.contact ? [activity.contact.id] : []),
    recurInterval,
    recurIntervalType: getRecurIntervalType(recurInterval),
    reminders: activity?.reminders ?? localSettings.getActivityCreationNotifyPreferences() ?? [],
    startAt,
    subjectUserId: activity?.subjectUserId ?? undefined,
    teamIds: activity?.teamId,
    visibility: activity?.visibility ?? defaultVisibility,
  };
};

export default getFormInitialValues;
